import React, { useEffect, useState } from 'react';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { useSelector } from 'react-redux';
import { Button, Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import countries from 'i18n-iso-countries';
import { useRecoilValue } from 'recoil';
import CloseIcon from 'mdi-react/CloseIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import BriefcaseOutlineIcon from 'mdi-react/BriefcaseOutlineIcon';
import AccountOutlineIcon from 'mdi-react/AccountOutlineIcon';
import EarthIcon from 'mdi-react/EarthIcon';
import EyeOutlineIcon from 'mdi-react/EyeOutlineIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import RepeatIcon from 'mdi-react/RepeatIcon';
import CalendarBlankOutlineIcon from 'mdi-react/CalendarBlankOutlineIcon';
import CalendarMultipleIcon from 'mdi-react/CalendarMultipleIcon';
import CalendarRangeIcon from 'mdi-react/CalendarRangeIcon';
import renderField from '../../../../shared/components/custom/Field';
import renderSelectField from '../../../../shared/components/form/Select';
import renderDropZoneField from '../../../../shared/components/form/DropZoneMS';
import FormBox from '../../../../shared/components/custom/FormBox';
import ChoiceCards from '../../../../shared/components/custom/ChoiceCards';
import SwitchRow from '../../../../shared/components/custom/SwitchRow';
import InfoBox from '../../../../shared/components/custom/InfoBox';
import TooltipWithIcon from '../../../../shared/components/custom/TooltipWithIcon';
import SquareIconButton from '../../../../shared/components/custom/SquareIconButton';
import TextEditor2 from '../../../../shared/components/text-editor/TextEditor2';
import t, { partial } from '../../../../util/translation/translation';
import userLanguageState from '../../../../recoil/userLanguage/selector';
import EventTypes from '../../types';
import {
  BOOKING_MODES,
  ISO_WEEKDAYS,
  weekdayName,
  formatDay,
} from '../seriesUtils';
import {
  SERIES_SECTIONS,
  newSeriesValues,
  seriesToFormValues,
  validateSeries,
} from '../seriesFormValues';

countries.registerLocale(require('i18n-iso-countries/langs/nb.json'));
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const NOT_APPLICABLE = { label: 'N/A', value: null };

const BOOKING_MODE_ICONS = {
  occurrence: CalendarBlankOutlineIcon,
  series: CalendarMultipleIcon,
  both: CalendarRangeIcon,
};

const FieldError = ({ meta }) =>
  (meta.touched || meta.submitFailed) && meta.error ? (
    <span className="form__form-group-error">{meta.error}</span>
  ) : null;

// The days of the week as toggles; the value is ISO weekdays (1 = Monday)
const WeekdaysField = ({ input, meta }) => {
  const days = Array.isArray(input.value) ? input.value : [];
  const toggle = (day) =>
    input.onChange(
      days.includes(day)
        ? days.filter((existing) => existing !== day)
        : [...days, day].sort()
    );
  return (
    <div className="form__form-group-input-wrap">
      <div className="series-form__chips">
        {ISO_WEEKDAYS.map((day) => (
          <button
            key={day}
            type="button"
            className={`stats-range${days.includes(day) ? ' active' : ''}`}
            aria-pressed={days.includes(day)}
            onClick={() => toggle(day)}
          >
            {weekdayName(day)}
          </button>
        ))}
      </div>
      <FieldError meta={meta} />
    </div>
  );
};

// One or more fixed start times ('HH:mm')
const TimesField = ({ input, meta }) => {
  const p = partial('EventSeries');
  const times =
    Array.isArray(input.value) && input.value.length ? input.value : [''];
  const setTime = (index, value) =>
    input.onChange(times.map((time, i) => (i === index ? value : time)));
  return (
    <div className="form__form-group-input-wrap">
      <div className="series-form__times">
        {times.map((time, index) => (
          <div className="series-form__time" key={index}>
            <input
              type="time"
              value={time}
              aria-label={p('startTime')}
              onChange={(event) => setTime(index, event.target.value)}
            />
            {times.length > 1 && (
              <SquareIconButton
                icon={<CloseIcon />}
                title={p('removeTime')}
                onClick={() =>
                  input.onChange(times.filter((_, i) => i !== index))
                }
              />
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-0"
          onClick={() => input.onChange([...times, ''])}
        >
          <PlusIcon size={14} /> {p('addTime')}
        </button>
      </div>
      <FieldError meta={meta} />
    </div>
  );
};

// Days without occurrences, e.g. holidays ('YYYY-MM-DD')
const SkipDatesField = ({ input }) => {
  const p = partial('EventSeries');
  const [draft, setDraft] = useState('');
  const dates = Array.isArray(input.value) ? input.value : [];
  const add = () => {
    if (draft && !dates.includes(draft)) {
      input.onChange([...dates, draft].sort());
    }
    setDraft('');
  };
  return (
    <div className="form__form-group-input-wrap">
      <div className="series-form__skip-add">
        <input
          type="date"
          value={draft}
          aria-label={p('skipDates')}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-0"
          onClick={add}
          disabled={!draft}
        >
          {p('add')}
        </button>
      </div>
      {dates.length > 0 && (
        <div className="series-form__chips mt-2">
          {dates.map((date) => (
            <span className="series-chip" key={date}>
              {formatDay(date)}
              <button
                type="button"
                className="series-chip__remove"
                aria-label={p('removeDate', { date: formatDay(date) })}
                onClick={() =>
                  input.onChange(dates.filter((existing) => existing !== date))
                }
              >
                <CloseIcon size={14} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// The hint goes under the field, so fields side by side stay level
const FormGroup = ({ label, hint, children }) => (
  <div className="form__form-group">
    <span className="form-header">{label}</span>
    {children}
    {hint && <small className="series-form__hint">{hint}</small>}
  </div>
);

// The series form: the event fields every occurrence shares ('details') and
// the schedule with its booking rules ('schedule'). Creating a series shows
// both; the series page edits each on its own tab.
const SeriesForm = (props) => {
  const {
    handleSubmit,
    initialize,
    change,
    form,
    series,
    sections,
    eventCategories,
    eventLevels,
    eventGroups,
    eventTiers,
    submitLabel,
    onCancel,
    children,
  } = props;
  const p = partial('EventSeries');
  const s = partial('shared');
  const ef = partial('EventsForm');
  const o = partial('OfferForm');
  const values = useSelector((state) => getFormValues(form)(state)) || {};
  const userLanguage = useRecoilValue(userLanguageState);
  const { TargetAudience } = EventTypes;
  const isNew = !series;
  const showDetails = sections.includes('details');
  const showSchedule = sections.includes('schedule');

  const noCategoryOption = { label: s('noneEvents'), value: null };
  const allLabels = {
    eventLevels: { label: ef('allLevels'), value: [] },
    eventGroups: { label: ef('allGroups'), value: [] },
    eventTiers: { label: ef('allTiers'), value: [] },
  };
  const countryOptions = Object.entries(countries.getNames(userLanguage)).map(
    ([iso, country]) => ({ name: country, iso, value: iso, label: country })
  );

  useEffect(() => {
    if (series) {
      initialize(
        seriesToFormValues(series, {
          noCategoryOption,
          accessOptions: {
            levels: eventLevels,
            groups: eventGroups,
            tiers: eventTiers,
          },
          allLabels,
          countryOptions,
        })
      );
    } else {
      initialize(newSeriesValues(noCategoryOption));
    }
  }, [series, eventLevels, eventGroups, eventTiers]);

  // Picking "N/A" or "All" clears the other picks, and picking one after
  // them replaces them (as on the event form)
  useEffect(() => {
    ['eventLevels', 'eventGroups', 'eventTiers'].forEach((fieldName) => {
      const selected = values[fieldName];
      if (!Array.isArray(selected) || selected.length < 2) return;
      const [first, ...rest] = selected;
      const newest = selected[selected.length - 1];
      if (newest.value === null || Array.isArray(newest.value)) {
        change(fieldName, [newest]);
      } else if (first.value === null || Array.isArray(first.value)) {
        change(fieldName, rest);
      }
    });
  }, [values.eventLevels, values.eventGroups, values.eventTiers]);

  const handleChangeAudience = () => {
    change('eventLevels', [NOT_APPLICABLE]);
    change('eventGroups', [NOT_APPLICABLE]);
    change('eventTiers', [NOT_APPLICABLE]);
    // Categories are for partners only
    change('eventCategories', noCategoryOption);
  };

  const targetAudience = values.targetAudience;
  const hasCategory = !!values.eventCategories?.value;
  const isInterval = values.scheduleKind === 'interval';

  const renderDetails = () => (
    <>
      <Col xs={12} className="mb-3">
        <FormBox title={s('details')}>
          <Col md={12}>
            <FormGroup label={s('title')}>
              <Field
                name="title"
                placeholder={s('title')}
                component={renderField}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup label={ef('description')}>
              <Field
                header={[2, 3, false]}
                name="description"
                placeholder={ef('descriptionOfEvent')}
                component={TextEditor2}
              />
            </FormGroup>
          </Col>
        </FormBox>
      </Col>
      <Col xs={12} className="mb-3">
        <FormBox title={s('access')}>
          <Col md={12}>
            <FormGroup label={o('targetAudience')}>
              <Field
                name="targetAudience"
                component={ChoiceCards}
                ariaLabel={o('targetAudience')}
                columns={3}
                onChange={handleChangeAudience}
                disabled={!isNew}
                options={[
                  {
                    value: TargetAudience.partner,
                    icon: BriefcaseOutlineIcon,
                    label: o('partner'),
                  },
                  {
                    value: TargetAudience.private,
                    icon: AccountOutlineIcon,
                    label: o('private'),
                  },
                  {
                    value: TargetAudience.all,
                    icon: EarthIcon,
                    label: s('all'),
                  },
                ]}
              />
            </FormGroup>
          </Col>
          {targetAudience === TargetAudience.partner && (
            <Col md={12}>
              <FormGroup
                label={`${ef('chooseCategoryEvent')}${
                  values.eventCategories?.maximum
                    ? ` (${t('EventCategories.maxParticipants')}: ${
                        values.eventCategories.maximum
                      })`
                    : ''
                }`}
              >
                <Field
                  name="eventCategories"
                  placeholder={ef('chooseCategoryEvent')}
                  options={[noCategoryOption, ...eventCategories]}
                  component={renderSelectField}
                />
              </FormGroup>
            </Col>
          )}
          {hasCategory ? (
            <Col md={12}>
              <InfoBox>{p('guestsFromCategory')}</InfoBox>
            </Col>
          ) : (
            <div className="flex">
              <Col md={6}>
                <FormGroup label={p('placesPerOccurrence')}>
                  <Field
                    name="maxAttendees"
                    component={renderField}
                    placeholder={p('placesPlaceholder')}
                    type="number"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup label={p('maxGuests')} hint={p('maxGuestsHint')}>
                  <Field
                    name="maxGuests"
                    type="number"
                    component={renderField}
                  />
                </FormGroup>
              </Col>
            </div>
          )}
          {targetAudience === TargetAudience.partner && (
            <>
              <Col md={12}>
                <FormGroup label={ef('chooseLevelForEvent')}>
                  <Field
                    name="eventLevels"
                    placeholder={ef('chooseLevelForEvent')}
                    options={[
                      NOT_APPLICABLE,
                      allLabels.eventLevels,
                      ...eventLevels,
                    ]}
                    component={renderSelectField}
                    multiple
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup label={ef('chooseUsergroupAsEvent')}>
                  <Field
                    name="eventGroups"
                    placeholder={ef('chooseUsergroupAsEvent')}
                    options={[
                      NOT_APPLICABLE,
                      allLabels.eventGroups,
                      ...eventGroups,
                    ]}
                    component={renderSelectField}
                    multiple
                  />
                </FormGroup>
              </Col>
            </>
          )}
          {targetAudience === TargetAudience.private && (
            <Col md={12}>
              <FormGroup label={ef('chooseTierForEvent')}>
                <Field
                  name="eventTiers"
                  placeholder={ef('chooseTierForEvent')}
                  options={[
                    NOT_APPLICABLE,
                    allLabels.eventTiers,
                    ...eventTiers,
                  ]}
                  component={renderSelectField}
                  multiple
                />
              </FormGroup>
            </Col>
          )}
          {targetAudience !== TargetAudience.all && (
            <Col md={12}>
              <FormGroup
                label={
                  <span className="flex">
                    {ef('visibility')}
                    <TooltipWithIcon
                      text={ef('visibilityInfoTooltip')}
                      id="seriesVisibilityInfoTooltip"
                    />
                  </span>
                }
              >
                <Field
                  name="feedVisibility"
                  component={ChoiceCards}
                  ariaLabel={ef('visibility')}
                  options={[
                    { value: 'ALL', icon: EyeOutlineIcon, label: s('all') },
                    {
                      value: 'ONLYINVITED',
                      icon: LockOutlineIcon,
                      label: ef('onlyInvited'),
                    },
                  ]}
                />
              </FormGroup>
            </Col>
          )}
        </FormBox>
      </Col>
      <Col xs={12} className="mb-3">
        <FormBox title={s('uploadImage')}>
          <Field
            name="imageUrl"
            className="dropzone"
            maxSize={3}
            component={renderDropZoneField}
            crop="BANNER_CROP"
          />
          <small style={{ padding: 10 }}>
            <strong>{s('note')}:</strong> {s('recommendedImageSize')} 1035 *
            582px, 16:9 {s('ratio')}
          </small>
        </FormBox>
      </Col>
      <Col xs={12} className="mb-3">
        <FormBox title={s('optional')}>
          <div className="flex">
            <Col md={6}>
              <FormGroup label={s('link')}>
                <Field
                  name="websiteUrl"
                  placeholder={s('link')}
                  component={renderField}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup label={ef('linkName')}>
                <Field
                  name="urlText"
                  placeholder={s('name')}
                  component={renderField}
                />
              </FormGroup>
            </Col>
          </div>
          <Col md={12}>
            <FormGroup label={s('address')}>
              <Field
                name="address"
                placeholder={s('address')}
                component={renderField}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup label={ef('googleMapsLink')}>
              <Field
                name="mapsUrl"
                placeholder={ef('googleMapsLink')}
                component={renderField}
              />
            </FormGroup>
          </Col>
          <div className="flex">
            <Col md={4}>
              <FormGroup label={ef('zip')}>
                <Field
                  name="zip"
                  placeholder={ef('zip')}
                  component={renderField}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup label={ef('city')}>
                <Field
                  name="city"
                  placeholder={ef('city')}
                  component={renderField}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup label={ef('country')}>
                <Field
                  name="country"
                  component={renderSelectField}
                  options={countryOptions}
                  placeholder={s('chooseCountry')}
                />
              </FormGroup>
            </Col>
          </div>
          <Col md={12}>
            <Field
              name="participantsVisible"
              component={SwitchRow}
              label={t('EventsPage.participantsVisible')}
            />
          </Col>
        </FormBox>
      </Col>
    </>
  );

  const renderSchedule = () => (
    <>
      <Col xs={12} className="mb-3">
        <FormBox title={p('schedule')}>
          <Col md={12}>
            <FormGroup label={p('times')}>
              <Field
                name="scheduleKind"
                component={ChoiceCards}
                ariaLabel={p('times')}
                options={[
                  {
                    value: 'weekly',
                    icon: ClockOutlineIcon,
                    label: p('kindWeekly'),
                  },
                  {
                    value: 'interval',
                    icon: RepeatIcon,
                    label: p('kindInterval'),
                  },
                ]}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup label={p('days')}>
              <Field name="weekdays" component={WeekdaysField} />
            </FormGroup>
          </Col>
          {isInterval ? (
            <div className="flex">
              <Col md={4}>
                <FormGroup label={p('firstStart')}>
                  <Field
                    name="intervalFrom"
                    type="time"
                    component={renderField}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup label={p('lastStart')} hint={p('lastStartHint')}>
                  <Field
                    name="intervalTo"
                    type="time"
                    component={renderField}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup label={p('everyMinutes')}>
                  <Field
                    name="everyMinutes"
                    type="number"
                    component={renderField}
                  />
                </FormGroup>
              </Col>
            </div>
          ) : (
            <Col md={12}>
              <FormGroup label={p('startTimes')}>
                <Field name="times" component={TimesField} />
              </FormGroup>
            </Col>
          )}
          <div className="flex">
            <Col md={4}>
              <FormGroup label={p('duration')}>
                <Field
                  name="durationMinutes"
                  type="number"
                  component={renderField}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup label={p('firstDate')}>
                <Field name="startDate" type="date" component={renderField} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup label={p('lastDate')} hint={p('lastDateHint')}>
                <Field name="endDate" type="date" component={renderField} />
              </FormGroup>
            </Col>
          </div>
          <Col md={4}>
            <FormGroup label={p('weeksAhead')} hint={p('weeksAheadHint')}>
              <Field name="weeksAhead" type="number" component={renderField} />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup label={p('skipDates')} hint={p('skipDatesHint')}>
              <Field name="skipDates" component={SkipDatesField} />
            </FormGroup>
          </Col>
        </FormBox>
      </Col>
      <Col xs={12} className="mb-3">
        <FormBox title={p('bookingRules')}>
          <Col md={12}>
            <FormGroup label={p('bookingMode')}>
              <Field
                name="bookingMode"
                component={ChoiceCards}
                ariaLabel={p('bookingMode')}
                columns={1}
                options={BOOKING_MODES.map((mode) => ({
                  value: mode,
                  icon: BOOKING_MODE_ICONS[mode],
                  label: p(`bookingMode-${mode}`),
                  hint: p(`bookingModeHint-${mode}`),
                }))}
              />
            </FormGroup>
          </Col>
          <div className="flex">
            <Col md={6}>
              <FormGroup
                label={p('opensDaysBefore')}
                hint={p('opensDaysBeforeHint')}
              >
                <Field
                  name="registrationOpensDaysBefore"
                  type="number"
                  component={renderField}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup
                label={p('closesHoursBefore')}
                hint={p('closesHoursBeforeHint')}
              >
                <Field
                  name="registrationClosesHoursBefore"
                  type="number"
                  component={renderField}
                />
              </FormGroup>
            </Col>
          </div>
          <Col md={12}>
            <Field
              name="waitlistEnabled"
              component={SwitchRow}
              label={t('EventsPage.enableWaitlist')}
            />
            <Field
              name="bindingRegistration"
              component={SwitchRow}
              label={t('EventsPage.bindingRegistration')}
              description={t('EventsPage.bindingRegistrationLabel')}
            />
          </Col>
        </FormBox>
      </Col>
    </>
  );

  return (
    <form onSubmit={handleSubmit} className="form series-form">
      <Row className="w-100 mx-0">
        {showDetails && renderDetails()}
        {showSchedule && renderSchedule()}
        {children && (
          <Col xs={12} className="mb-3">
            {children}
          </Col>
        )}
        <Col xs={12}>
          <Button color="primary" type="submit">
            {submitLabel || (isNew ? p('createSeries') : s('save'))}
          </Button>
          {onCancel && (
            <Button color="secondary" type="button" onClick={onCancel}>
              {s('close')}
            </Button>
          )}
        </Col>
      </Row>
    </form>
  );
};

SeriesForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  series: PropTypes.shape({}),
  sections: PropTypes.arrayOf(PropTypes.oneOf(SERIES_SECTIONS)),
  eventCategories: PropTypes.arrayOf(PropTypes.shape({})),
  eventLevels: PropTypes.arrayOf(PropTypes.shape({})),
  eventGroups: PropTypes.arrayOf(PropTypes.shape({})),
  eventTiers: PropTypes.arrayOf(PropTypes.shape({})),
  submitLabel: PropTypes.string,
  onCancel: PropTypes.func,
  children: PropTypes.node,
};

SeriesForm.defaultProps = {
  series: null,
  sections: SERIES_SECTIONS,
  eventCategories: [],
  eventLevels: [],
  eventGroups: [],
  eventTiers: [],
  submitLabel: undefined,
  onCancel: undefined,
  children: null,
};

// The form name is given by each user (form="..."), so the series page can
// show the details and the schedule as two independent forms
export default reduxForm({
  validate: validateSeries,
})(SeriesForm);
